import { VISIT_HISTORY_CHANGE_EVENT } from '../hooks/useProjectVisitHistory';

interface ProjectVisitRecord {
    projectId: string;
    lastVisitedAt: number; // timestamp
}

const STORAGE_KEY = 'project_visit_history';
const MAX_HISTORY_SIZE = 50; // 최대 저장할 프로젝트 수

/**
 * 프로젝트 방문 기록을 로컬스토리지에 저장
 */
export const recordProjectVisit = (projectId: string): void => {
    try {
        const existingHistory = getProjectVisitHistory();
        const now = Date.now();

        // 기존 기록에서 해당 프로젝트 제거 (중복 방지)
        const filteredHistory = existingHistory.filter(
            record => record.projectId !== projectId
        );

        // 새 기록을 맨 앞에 추가
        const updatedHistory: ProjectVisitRecord[] = [
            { projectId, lastVisitedAt: now },
            ...filteredHistory,
        ].slice(0, MAX_HISTORY_SIZE); // 최대 저장 수 제한

        localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedHistory));

        // 방문 기록 변경을 다른 컴포넌트에 알림
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent(VISIT_HISTORY_CHANGE_EVENT));
        }
    } catch (error) {
        console.warn('Failed to record project visit:', error);
    }
};

/**
 * 프로젝트 방문 기록을 로컬스토리지에서 조회
 */
export const getProjectVisitHistory = (): ProjectVisitRecord[] => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) return [];

        const history: ProjectVisitRecord[] = JSON.parse(
            stored
        ) as ProjectVisitRecord[];
        return Array.isArray(history) ? history : [];
    } catch (error) {
        console.warn('Failed to get project visit history:', error);
        return [];
    }
};

/**
 * 특정 프로젝트의 마지막 방문 시간 조회
 */
export const getProjectLastVisitTime = (projectId: string): number | null => {
    const history = getProjectVisitHistory();
    const record = history.find(r => r.projectId === projectId);
    return record?.lastVisitedAt || null;
};

/**
 * 프로젝트 배열을 최근 방문 순으로 정렬
 * 최근 방문한 프로젝트가 앞에 오고, 방문 기록이 없는 프로젝트는 원래 순서 유지
 */
export const sortProjectsByRecentVisit = <T extends { id: string }>(
    projects: T[]
): T[] => {
    const visitHistory = getProjectVisitHistory();
    const visitMap = new Map(
        visitHistory.map(record => [record.projectId, record.lastVisitedAt])
    );

    return [...projects].sort((a, b) => {
        const aVisitTime = visitMap.get(a.id);
        const bVisitTime = visitMap.get(b.id);

        // 둘 다 방문 기록이 있는 경우: 최근 방문 순
        if (aVisitTime && bVisitTime) {
            return bVisitTime - aVisitTime;
        }

        // a만 방문 기록이 있는 경우: a가 앞에
        if (aVisitTime && !bVisitTime) {
            return -1;
        }

        // b만 방문 기록이 있는 경우: b가 앞에
        if (!aVisitTime && bVisitTime) {
            return 1;
        }

        // 둘 다 방문 기록이 없는 경우: 원래 순서 유지
        return 0;
    });
};
